/**
 * Sidebar icon catalog — single source of truth for portal sidebar
 * + megamenu link icons. Ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/sidebarIcons.tsx}.
 *
 * <p>Each entry carries:
 * <ul>
 *   <li>{@code name} — stable id stored in the menu item's
 *       {@code icon} field</li>
 *   <li>{@code label} — human-readable picker caption. NOT stored on the
 *       entry: it comes from this package's i18n catalogue under
 *       {@code icons.<name>} and is attached by {@code withLabel} when the
 *       catalog is read, so the picker follows the active language</li>
 *   <li>{@code Icon} — the {@code lucide-react} component (statically
 *       imported so the bundler can tree-shake out unused icons)</li>
 *   <li>{@code aliases} — optional search aliases for the picker</li>
 * </ul>
 *
 * <h3>Adding a new icon</h3>
 * <ol>
 *   <li>Pick a lucide-react icon from https://lucide.dev/icons</li>
 *   <li>Add a static import below</li>
 *   <li>Append an entry to {@code ICON_DEFS} (alphabetical-ish within
 *       section)</li>
 *   <li>Add its caption to {@code locales/{tr,en}.json} under
 *       {@code icons.<name>}</li>
 *   <li>Optionally extend {@link guessSidebarIcon} so authors who never
 *       set an icon get a plausible auto-pick from the menu label</li>
 * </ol>
 *
 * <p>The icon set is intentionally curated — adding lucide's full
 * ~1500 icons would bloat the bundle and overwhelm the picker. The
 * curated ~70 cover navigation use cases for OTT, learning,
 * marketing, commerce and community products.
 */
import {
  Home,
  Compass,
  Search,
  Library,
  Bookmark,
  Clock,
  Star,
  Heart,
  TrendingUp,
  Flame,
  Play,
  Film,
  Video,
  Tv,
  Music,
  Headphones,
  Mic,
  Radio,
  Podcast,
  Image,
  Grid3x3,
  LayoutGrid,
  List,
  Folder,
  Layers,
  BookOpen,
  Newspaper,
  Calendar,
  Bell,
  User,
  Users,
  UserCircle,
  MessageSquare,
  Mail,
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  Wallet,
  DollarSign,
  Tag,
  Ticket,
  Gift,
  Percent,
  Gamepad2,
  Trophy,
  Award,
  BadgeCheck,
  Sparkles,
  Bot,
  Brain,
  Zap,
  Rocket,
  Code,
  FlaskConical,
  Target,
  HelpCircle,
  Info,
  Lock,
  Shield,
  Key,
  Settings,
  Sliders,
  Wrench,
  Globe,
  Map,
  Briefcase,
  Building,
  Flag,
  Menu,
  PanelLeft,
  PanelRight,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  X,
  type LucideIcon,
} from 'lucide-react';
import { DynamicIcon, iconNames as LUCIDE_ICON_NAMES, type IconName } from 'lucide-react/dynamic';
import { decodeHtmlPayload } from '../../portal/htmlPayload';
import { sbText } from '../../portal/i18nCatalogue';

export interface SidebarIconDef {
  /** Stable id stored in {@code header.menu[i].icon}. */
  name: string;
  /** Human-readable label shown in the picker tile. */
  label: string;
  /** The lucide-react component that renders the icon. */
  Icon: LucideIcon;
  /** Search aliases — picker also matches any of these substrings. */
  aliases?: string[];
}

/**
 * A catalogue entry as authored. The picker caption is NOT stored here:
 * it lives in the package's i18n catalogue under {@code icons.<name>} and
 * is attached by {@link withLabel} at read time, so the grid follows a
 * language switch instead of freezing whichever language was active when
 * this module first evaluated.
 */
type IconCatalogEntry = Omit<SidebarIconDef, 'label'>;

const ICON_DEFS: IconCatalogEntry[] = [
  // Navigation / discovery
  { name: 'home', Icon: Home, aliases: ['house', 'index'] },
  { name: 'compass', Icon: Compass, aliases: ['browse', 'explore'] },
  { name: 'search', Icon: Search, aliases: ['find', 'magnifier'] },
  { name: 'library', Icon: Library, aliases: ['shelf', 'collection'] },
  { name: 'bookmark', Icon: Bookmark, aliases: ['saved', 'watchlist'] },
  { name: 'clock', Icon: Clock, aliases: ['history', 'recent'] },
  { name: 'star', Icon: Star, aliases: ['featured', 'favorite'] },
  { name: 'heart', Icon: Heart, aliases: ['like', 'love'] },
  { name: 'trending-up', Icon: TrendingUp, aliases: ['popular', 'top'] },
  { name: 'flame', Icon: Flame, aliases: ['hot', 'fire'] },
  // Content / media
  { name: 'play', Icon: Play, aliases: ['watch', 'video'] },
  { name: 'film', Icon: Film, aliases: ['movie', 'cinema'] },
  { name: 'video', Icon: Video, aliases: ['clip', 'recording'] },
  { name: 'tv', Icon: Tv, aliases: ['television', 'show', 'series'] },
  { name: 'music', Icon: Music, aliases: ['song', 'audio'] },
  { name: 'headphones', Icon: Headphones, aliases: ['audio'] },
  { name: 'mic', Icon: Mic, aliases: ['record'] },
  { name: 'radio', Icon: Radio, aliases: ['live', 'broadcast'] },
  { name: 'podcast', Icon: Podcast, aliases: ['audio show'] },
  { name: 'image', Icon: Image, aliases: ['photo', 'picture'] },
  // Layouts / collections
  { name: 'grid', Icon: Grid3x3, aliases: ['catalog', 'tiles'] },
  { name: 'layout-grid', Icon: LayoutGrid, aliases: ['cards'] },
  { name: 'list', Icon: List, aliases: ['rows'] },
  { name: 'folder', Icon: Folder, aliases: ['directory'] },
  { name: 'layers', Icon: Layers, aliases: ['stacks'] },
  { name: 'book-open', Icon: BookOpen, aliases: ['read', 'docs', 'course'] },
  { name: 'newspaper', Icon: Newspaper, aliases: ['news', 'press'] },
  // Time / schedule
  { name: 'calendar', Icon: Calendar, aliases: ['schedule', 'events'] },
  { name: 'bell', Icon: Bell, aliases: ['notifications', 'alerts'] },
  // People / social
  { name: 'user', Icon: User, aliases: ['profile', 'account'] },
  { name: 'users', Icon: Users, aliases: ['community', 'team'] },
  { name: 'user-circle', Icon: UserCircle, aliases: ['profile'] },
  { name: 'message-square', Icon: MessageSquare, aliases: ['chat', 'comment'] },
  { name: 'mail', Icon: Mail, aliases: ['email', 'inbox', 'subscription'] },
  // Commerce / membership
  { name: 'shopping-cart', Icon: ShoppingCart, aliases: ['checkout', 'buy'] },
  { name: 'shopping-bag', Icon: ShoppingBag, aliases: ['shop'] },
  { name: 'credit-card', Icon: CreditCard, aliases: ['payment'] },
  { name: 'wallet', Icon: Wallet, aliases: ['balance'] },
  { name: 'dollar-sign', Icon: DollarSign, aliases: ['price', 'money', 'plan'] },
  { name: 'tag', Icon: Tag, aliases: ['label', 'category'] },
  { name: 'ticket', Icon: Ticket, aliases: ['voucher', 'pass'] },
  { name: 'gift', Icon: Gift, aliases: ['present', 'reward'] },
  { name: 'percent', Icon: Percent, aliases: ['offer', 'sale'] },
  // Games / achievements
  { name: 'gamepad-2', Icon: Gamepad2, aliases: ['gaming', 'gamification'] },
  { name: 'trophy', Icon: Trophy, aliases: ['win', 'achievement'] },
  { name: 'award', Icon: Award, aliases: ['badge'] },
  {
    name: 'badge-check',
    Icon: BadgeCheck,
    aliases: ['certificate', 'verified', 'diploma'],
  },
  // AI / tech
  { name: 'sparkles', Icon: Sparkles, aliases: ['ai', 'magic', 'assistant'] },
  { name: 'bot', Icon: Bot, aliases: ['assistant', 'robot'] },
  { name: 'brain', Icon: Brain, aliases: ['intelligence', 'ai'] },
  { name: 'zap', Icon: Zap, aliases: ['fast', 'energy', 'power'] },
  { name: 'rocket', Icon: Rocket, aliases: ['launch'] },
  { name: 'code', Icon: Code, aliases: ['developer', 'programming', 'software'] },
  {
    name: 'flask-conical',
    Icon: FlaskConical,
    aliases: ['experiment', 'practice', 'project'],
  },
  { name: 'target', Icon: Target, aliases: ['goal', 'aim'] },
  // Help / security
  { name: 'help-circle', Icon: HelpCircle, aliases: ['support', 'faq'] },
  { name: 'info', Icon: Info, aliases: ['about'] },
  { name: 'lock', Icon: Lock, aliases: ['private', 'secure'] },
  { name: 'shield', Icon: Shield, aliases: ['protected', 'safe'] },
  { name: 'key', Icon: Key, aliases: ['access'] },
  // Settings / admin
  { name: 'settings', Icon: Settings, aliases: ['preferences', 'cog'] },
  { name: 'sliders', Icon: Sliders, aliases: ['controls'] },
  { name: 'wrench', Icon: Wrench, aliases: ['admin'] },
  // Geography / business
  { name: 'globe', Icon: Globe, aliases: ['world', 'language', 'locale'] },
  { name: 'map', Icon: Map, aliases: ['location'] },
  { name: 'briefcase', Icon: Briefcase, aliases: ['work', 'business'] },
  { name: 'building', Icon: Building, aliases: ['organization', 'company'] },
  { name: 'flag', Icon: Flag, aliases: ['country', 'mark'] },
  // Toggle icons (used by collapse / expand buttons)
  { name: 'menu', Icon: Menu, aliases: ['burger', 'lines'] },
  { name: 'panel-left', Icon: PanelLeft, aliases: ['sidebar'] },
  { name: 'panel-right', Icon: PanelRight, aliases: ['sidebar'] },
  {
    name: 'panel-left-close',
    Icon: PanelLeftClose,
    aliases: ['collapse', 'shrink'],
  },
  {
    name: 'panel-left-open',
    Icon: PanelLeftOpen,
    aliases: ['expand', 'enlarge'],
  },
  { name: 'chevrons-left', Icon: ChevronsLeft, aliases: ['collapse'] },
  { name: 'chevrons-right', Icon: ChevronsRight, aliases: ['expand'] },
  { name: 'chevron-left', Icon: ChevronLeft, aliases: ['back', 'collapse'] },
  {
    name: 'chevron-right',
    Icon: ChevronRight,
    aliases: ['forward', 'expand'],
  },
  { name: 'x', Icon: X, aliases: ['cross', 'dismiss'] },
];

const ICON_INDEX: Record<string, IconCatalogEntry> = Object.fromEntries(
  ICON_DEFS.map((d) => [d.name, d]),
);

/** Attach the picker caption for the ACTIVE language. Called per read,
 *  never at module scope — see {@link IconCatalogEntry}. */
function withLabel(def: IconCatalogEntry): SidebarIconDef {
  return { ...def, label: sbText(`icons.${def.name}`) };
}

// The full lucide name list is only needed by the admin picker's
// "search everything" mode — never on the render hot path. We read it
// LAZILY (not at module-eval) and DEFENSIVELY: lucide's `lucide-react/dynamic`
// subpath has no package `exports` map and ships a `.js` star-reexport shim,
// so under some bundler interop the `iconNames` binding doesn't arrive as an
// array. Iterating it at import time (`new Set(iconNames)`) then throws
// "function is not iterable" and crashes the whole bundle. Normalising to []
// keeps every consumer safe; the picker just falls back to curated-only.
let _allLucideNames: string[] | null = null;
let _lucideNameSet: Set<string> | null = null;

/**
 * The full lucide catalog of icon names — for the admin picker's
 * "search everything" mode. The curated {@link listSidebarIcons} is the
 * fast-path favourites shown by default; this is the long tail.
 */
export function listAllLucideIconNames(): readonly string[] {
  if (_allLucideNames) return _allLucideNames;
  const raw = LUCIDE_ICON_NAMES as unknown;
  _allLucideNames = Array.isArray(raw) ? (raw as string[]) : [];
  return _allLucideNames;
}

/** True when {@code name} is a valid lucide icon id (curated or not). */
export function isLucideIconName(name: string): boolean {
  const id = toIconId(name);
  if (id in ICON_INDEX) return true;
  if (!_lucideNameSet) _lucideNameSet = new Set(listAllLucideIconNames());
  return _lucideNameSet.has(id);
}

/**
 * Render an icon by name, in priority order:
 *  1. Curated names render from a static import — instant and SSR-safe.
 *  2. Any other name renders via {@code DynamicIcon}, which lazy-loads the
 *     glyph on the client. It paints nothing on the server and during the
 *     first frame, then swaps the icon in once the chunk resolves — so
 *     prefer curated names for above-the-fold/SSR surfaces. DynamicIcon
 *     also swallows names it can't resolve (renders nothing), so we don't
 *     pre-check membership here — doing so would force the fragile full
 *     name list into module-eval (see the note above).
 *  3. Empty/no name falls back to {@code star} so callers always get
 *     something visible without breaking the layout.
 */
export function SidebarIcon({ name, size = 22 }: { name?: string | null; size?: number }) {
  // Both spellings resolve — see {@link toIconId}. Without the fold, a
  // PascalCase value misses the catalog AND DynamicIcon's own kebab index,
  // so the icon silently renders as nothing.
  const id = name ? toIconId(name) : '';
  const def = id ? ICON_INDEX[id] : undefined;
  if (def) {
    const Icon = def.Icon;
    return <Icon size={size} strokeWidth={2} />;
  }
  if (id) {
    return <DynamicIcon name={id as IconName} size={size} strokeWidth={2} />;
  }
  const Star = ICON_INDEX['star']!.Icon;
  return <Star size={size} strokeWidth={2} />;
}

/** A picker value is a bare icon id — no scheme, slash, dot, angle bracket
 *  or whitespace. Both spellings count: the kebab id the picker writes
 *  ("book-open") and the PascalCase React-component spelling lucide's own
 *  docs use ("BookOpen"), which is what hand-typed and seeded values carry.
 *  URLs and inline SVG fail this test and fall through to the other
 *  {@link BlockIcon} rendering paths. */
const ICON_NAME_RE = /^[A-Za-z][A-Za-z0-9-]*$/;

/**
 * Fold an icon value onto the kebab id the catalog and lucide's
 * {@code DynamicIcon} both index by: {@code "BookOpen"} and
 * {@code "Building2"} are the React component names for
 * {@code "book-open"} and {@code "building-2"}. Already-kebab ids pass
 * through untouched.
 */
export function toIconId(name: string): string {
  return name
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Za-z])(\d)/g, '$1-$2')
    .toLowerCase();
}

/** True when {@code value} is a bare icon id rather than a URL or inline
 *  SVG — the same test {@link BlockIcon} routes on, exported so the admin
 *  picker classifies a stored value exactly the way the portal renders it. */
export function looksLikeIconName(value: string): boolean {
  return ICON_NAME_RE.test(value.trim());
}

/**
 * Normalise a pasted SVG file into embeddable markup: drop everything a
 * standalone `.svg` file carries before the root element — the XML
 * declaration, a DOCTYPE, editor comments (Illustrator/Figma exports all
 * start with one of these) — then strip the two things the markup must
 * never carry: `<script>` blocks and inline `on*=` handlers. Operator-
 * authored, so the stripping is a guard rail rather than a trust boundary.
 *
 * Returns `null` when the input has no `<svg` root — the caller then treats
 * the value as an image URL.
 */
function toInlineSvg(raw: string): string | null {
  const start = raw.search(/<svg[\s>]/i);
  if (start < 0) return null;
  return ensureSvgViewBox(
    raw
      .slice(start)
      .replace(/<script[\s\S]*?<\/script\s*>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, ''),
  );
}

/**
 * Give the root `<svg>` a `viewBox` when it only carries `width`/`height`.
 * Without one there is no user-coordinate system to map onto the icon box,
 * so `width/height:100%` crops the top-left corner of the artwork's own
 * canvas instead of scaling it down. SVGO-style optimisers drop the
 * viewBox routinely, so this is the common case, not the edge case.
 */
function ensureSvgViewBox(svg: string): string {
  const openTag = svg.match(/^<svg\b[^>]*>/i)?.[0];
  if (!openTag || /\sviewBox\s*=/i.test(openTag)) return svg;

  const width = openTag.match(/\swidth\s*=\s*["']?([\d.]+)/i)?.[1];
  const height = openTag.match(/\sheight\s*=\s*["']?([\d.]+)/i)?.[1];
  if (!width || !height) return svg;

  return svg.replace(openTag, openTag.replace(/^<svg/i, `<svg viewBox="0 0 ${width} ${height}"`));
}

/**
 * Icon/image slot shared by every block whose "icon" field accepts three
 * value shapes, in priority order:
 *  1. A bare icon id — the picker's kebab slug ("book-open") or lucide's
 *     PascalCase component spelling ("BookOpen") → {@link SidebarIcon}.
 *  2. Inline `<svg>…</svg>` markup, pasted by the operator.
 *  3. Anything else — treated as an image URL / data URI.
 *
 * The value may be base64-encoded (`b64:…`, see {@link decodeHtmlPayload}) —
 * the admin's SVG-paste tab stores it that way because the backend strips
 * every angle bracket from plain string block props on save. Plain values
 * (icon names, URLs) pass through decode untouched, so this is safe to call
 * unconditionally regardless of which tab produced the stored value.
 *
 * This consolidates what used to be five near-identical local copies
 * (ButtonGroup, HeroTriColumn, HeroSplitRotator, LearningJourneyPlans,
 * TeamMembersBoardV2) into one shared implementation.
 */
export function BlockIcon({
  value,
  size = 20,
  className,
}: {
  value?: string | null;
  size?: number;
  className?: string;
}) {
  const trimmed = decodeHtmlPayload(value ?? '').trim();
  if (!trimmed) return <SidebarIcon size={size} />;

  if (ICON_NAME_RE.test(trimmed)) {
    return (
      <span
        aria-hidden="true"
        className={className}
        style={{ display: 'inline-flex', flexShrink: 0 }}
      >
        <SidebarIcon name={trimmed} size={size} />
      </span>
    );
  }

  const svg = toInlineSvg(trimmed);
  if (svg) {
    return (
      <span
        aria-hidden="true"
        className={className}
        style={{ display: 'inline-flex', width: size, height: size, flexShrink: 0 }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  return (
    <img
      src={trimmed}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      className={className}
      style={{ display: 'inline-block', flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

/** Whole curated catalog — the picker's default fast-path favourites. */
export function listSidebarIcons(): SidebarIconDef[] {
  return ICON_DEFS.map(withLabel);
}

/**
 * Search the catalog by name, label, or alias substring. Returns the
 * full list when the query is empty.
 */
export function searchSidebarIcons(query: string): SidebarIconDef[] {
  const q = query.trim().toLowerCase();
  const all = ICON_DEFS.map(withLabel);
  if (!q) return all;
  return all.filter(
    (d) =>
      d.name.toLowerCase().includes(q) ||
      d.label.toLowerCase().includes(q) ||
      (d.aliases ?? []).some((a) => a.toLowerCase().includes(q)),
  );
}

/**
 * Heuristic auto-pick from a menu label. Used by the portal renderer
 * when the author hasn't set an explicit icon, so menus look reasonable
 * out of the box.
 */
export function guessSidebarIcon(label: string): string {
  const l = label.toLowerCase();
  if (/home|feed/.test(l)) return 'home';
  if (/browse|explore|discover/.test(l)) return 'compass';
  if (/search|find/.test(l)) return 'search';
  if (/trending|popular|chart/.test(l)) return 'trending-up';
  if (/featured|favorite/.test(l)) return 'star';
  if (/like|love/.test(l)) return 'heart';
  if (/saved|watchlist|bookmark/.test(l)) return 'bookmark';
  if (/library/.test(l)) return 'library';
  if (/history|recent/.test(l)) return 'clock';
  if (/subscription/.test(l)) return 'mail';
  if (/play|watch/.test(l)) return 'play';
  if (/film|movie|cinema/.test(l)) return 'film';
  if (/video|clip/.test(l)) return 'video';
  if (/show|series|episode|tv|television/.test(l)) return 'tv';
  if (/music|song|track/.test(l)) return 'music';
  if (/audio|sound/.test(l)) return 'headphones';
  if (/podcast/.test(l)) return 'podcast';
  if (/radio|live|broadcast|stream/.test(l)) return 'radio';
  if (/photo|image|picture|gallery/.test(l)) return 'image';
  if (/calendar|event|schedul/.test(l)) return 'calendar';
  if (/notification|alert/.test(l)) return 'bell';
  if (/profile|account/.test(l)) return 'user-circle';
  if (/community|team|people|member/.test(l)) return 'users';
  if (/message|chat|comment|forum/.test(l)) return 'message-square';
  if (/mail|email|inbox/.test(l)) return 'mail';
  if (/cart|checkout|buy/.test(l)) return 'shopping-cart';
  if (/shop|store/.test(l)) return 'shopping-bag';
  if (/payment|card|billing/.test(l)) return 'credit-card';
  if (/wallet|balance/.test(l)) return 'wallet';
  if (/plan|pricing|subscribe|membership/.test(l)) return 'dollar-sign';
  if (/category|tag/.test(l)) return 'tag';
  if (/ticket|voucher|pass/.test(l)) return 'ticket';
  if (/gift|reward/.test(l)) return 'gift';
  if (/discount|offer|sale|coupon/.test(l)) return 'percent';
  if (/game|gamification|gaming/.test(l)) return 'gamepad-2';
  if (/trophy|achievement|win/.test(l)) return 'trophy';
  if (/award|badge|honor/.test(l)) return 'award';
  if (/ai\b|assistant|magic|smart/.test(l)) return 'sparkles';
  if (/bot|robot/.test(l)) return 'bot';
  if (/brain|intelligence/.test(l)) return 'brain';
  if (/help|support|faq/.test(l)) return 'help-circle';
  if (/info|about/.test(l)) return 'info';
  if (/private|secure/.test(l)) return 'lock';
  if (/protect|safe|security/.test(l)) return 'shield';
  if (/setting|preference|config/.test(l)) return 'settings';
  if (/control|adjust/.test(l)) return 'sliders';
  if (/admin|tool/.test(l)) return 'wrench';
  if (/world|global|language|locale/.test(l)) return 'globe';
  if (/map|location|where/.test(l)) return 'map';
  if (/work|business|career|job/.test(l)) return 'briefcase';
  if (/organization|company/.test(l)) return 'building';
  if (/news|press|article/.test(l)) return 'newspaper';
  if (/book|read|doc|guide|course|lesson|catalog/.test(l)) return 'book-open';
  if (/folder|directory/.test(l)) return 'folder';
  if (/grid|all|tiles/.test(l)) return 'grid';
  if (/list/.test(l)) return 'list';
  return 'star';
}
